import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, MapPin, Users } from 'lucide-react';
import { Card, Button } from '../components';
import api from '../services/api';
import type { EmployerLocation } from './EmployerOrgStructuur';
import type { OnderhandelingenOverzicht, OnderhandelingRij } from '../services/poolOnderhandelingApi';
import { OnderhandelingRijKaart } from './OnderhandelingRijKaart';

const CATEGORY_ORDER = ['psa', 'trauma', 'welzijn', 'ld', 'arbo'] as const;

/** Zelfde statussen als BEZETTE_STATUSSEN in poolOnderhandelingCockpitService. */
const BEZETTE_STATUSSEN = new Set(['AGREED', 'CONTRACT_PENDING', 'ACTIVE']);

const CATEGORY_LABELS: Record<string, string> = {
  psa: 'Mentale Druk & PSA',
  trauma: 'Trauma, Conflict & Escalatie',
  welzijn: 'Welzijn & Vitaliteit',
  ld: 'Loopbaan, Leiderschap & Ontwikkeling',
  arbo: 'Wettelijke Arbo-module',
};

interface Props {
  data: OnderhandelingenOverzicht;
  bezig: string | null;
  uitgeklapt: string | null;
  onToggleUitklap: (id: string) => void;
  onHerinner: (r: OnderhandelingRij) => void;
  onTrekIn: (r: OnderhandelingRij) => void;
  onAccepteerTegenbod: (r: OnderhandelingRij) => void;
  onTegenbodOpenen: (r: OnderhandelingRij) => void;
  onAfwijzenOpenen: (r: OnderhandelingRij) => void;
  onArchiveer: (r: OnderhandelingRij) => void;
  onTender: (r: OnderhandelingRij) => void;
  onOpenContract: (r: OnderhandelingRij) => void;
}

/**
 * Pool-Dekkingsdashboard: per vestiging en per functionele groep de
 * berekende staffel-behoefte (pool_demand_rules / gewenste_experts) naast
 * een live dekkingsbalk, met per discipline de uitklapbare
 * onderhandelings-matrix (zelfde rijen/acties als de platte lijst).
 */
export const PoolDekkingsDashboard: React.FC<Props> = ({
  data,
  bezig,
  uitgeklapt,
  onToggleUitklap,
  onHerinner,
  onTrekIn,
  onAccepteerTegenbod,
  onTegenbodOpenen,
  onAfwijzenOpenen,
  onArchiveer,
  onTender,
  onOpenContract,
}) => {
  const [locationId, setLocationId] = useState<string>('');
  const [opengeklapt, setOpengeklapt] = useState<Set<string>>(new Set());

  const { data: locaties } = useQuery({
    queryKey: ['employer-locations-dekkingsdashboard'],
    queryFn: async () => (await api.get('/employer/locations')).data?.data as EmployerLocation[],
  });

  const matrixVoorLocatie = useMemo(
    () => (locationId ? data.matrix.filter((r) => r.location_id === locationId) : data.matrix),
    [data.matrix, locationId]
  );

  const perType = useMemo(() => {
    const m = new Map<string, OnderhandelingRij[]>();
    for (const r of matrixVoorLocatie) {
      if (!m.has(r.professional_type)) m.set(r.professional_type, []);
      m.get(r.professional_type)!.push(r);
    }
    return m;
  }, [matrixVoorLocatie]);

  // De staffel (gewenst) is organisatiebreed; per vestiging tonen we welk deel
  // van de bezette plaatsen daar zit. Samenwerkingen zonder onderhandeling
  // hebben geen vestiging en tellen alleen organisatiebreed mee.
  const bezetOpLocatie = useMemo(() => {
    const m = new Map<string, number>();
    if (!locationId) return m;
    for (const r of matrixVoorLocatie) {
      if (r.gearchiveerd_op || !BEZETTE_STATUSSEN.has(r.engine_status)) continue;
      m.set(r.professional_type, (m.get(r.professional_type) || 0) + 1);
    }
    return m;
  }, [matrixVoorLocatie, locationId]);

  const groepen = useMemo(() => {
    const per = new Map<string, OnderhandelingenOverzicht['dekking']>();
    for (const d of data.dekking) {
      const cat = d.categorie || 'overig';
      if (!per.has(cat)) per.set(cat, []);
      per.get(cat)!.push(d);
    }
    const bekend = CATEGORY_ORDER.filter((c) => per.has(c));
    const onbekend = [...per.keys()].filter((c) => !CATEGORY_ORDER.includes(c as any));
    return [...bekend, ...onbekend].map((cat) => ({ cat, disciplines: per.get(cat)! }));
  }, [data.dekking]);

  const toggleDiscipline = (type: string) =>
    setOpengeklapt((s) => {
      const next = new Set(s);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });

  if (data.dekking.length === 0) {
    return (
      <Card>
        <p className="text-sm text-gray-500 text-center py-8">
          Nog geen pool ingericht — richt eerst uw pool in via de Pool-Wizard om hier een dekkingsdashboard te zien.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Vestiging-selector */}
      <div className="flex items-center gap-2">
        <MapPin className="w-4 h-4 text-gray-400" />
        <select
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
          className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
        >
          <option value="">Alle vestigingen</option>
          {(locaties || []).map((l) => (
            <option key={l.id} value={l.id}>
              {l.naam}
            </option>
          ))}
        </select>
      </div>

      {groepen.map(({ cat, disciplines }) => (
        <div key={cat}>
          <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
            {cat === 'arbo' && <span>🛡️</span>}
            {CATEGORY_LABELS[cat] || cat}
          </h3>
          <div className="space-y-3">
            {disciplines.map((d) => {
              const rijen = perType.get(d.professional_type) || [];
              const pct = d.gewenst > 0 ? Math.min(100, Math.round((d.bezet / d.gewenst) * 100)) : 0;
              const opLocatie = Math.min(bezetOpLocatie.get(d.professional_type) || 0, d.bezet);
              const pctLocatie = d.bezet > 0 ? Math.round((opLocatie / d.bezet) * pct) : 0;
              const isOpen = opengeklapt.has(d.professional_type);
              const barKleur = d.tekort > 0 ? 'bg-red-500' : pct >= 100 ? 'bg-emerald-500' : 'bg-blue-500';
              return (
                <Card key={d.professional_type} padding={false} className="overflow-hidden">
                  <button
                    type="button"
                    onClick={() => toggleDiscipline(d.professional_type)}
                    className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-gray-50"
                  >
                    {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-gray-900 truncate">{d.label}</p>
                        <span className="text-xs text-gray-500 flex items-center gap-1 flex-shrink-0">
                          <Users className="w-3.5 h-3.5" /> {d.bezet} / {d.gewenst}
                        </span>
                      </div>
                      <div className="mt-1.5 h-2 rounded-full bg-gray-100 overflow-hidden flex">
                        {locationId ? (
                          <>
                            <div className={`h-full ${barKleur}`} style={{ width: `${pctLocatie}%` }} />
                            <div className={`h-full ${barKleur} opacity-30`} style={{ width: `${pct - pctLocatie}%` }} />
                          </>
                        ) : (
                          <div className={`h-full rounded-full ${barKleur}`} style={{ width: `${pct}%` }} />
                        )}
                      </div>
                      {locationId && (
                        <p className="text-xs text-gray-500 mt-1">
                          {opLocatie} op deze vestiging · {d.bezet} / {d.gewenst} organisatiebreed
                        </p>
                      )}
                      {d.tekort > 0 && (
                        <p className="text-xs text-red-600 font-medium mt-1">
                          ⚠️ Tekort van {d.tekort} {d.tekort === 1 ? 'expert' : 'experts'} volgens de bedrijfsgrootte-staffel{locationId ? ' (organisatiebreed)' : ''}
                        </p>
                      )}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-gray-100 bg-gray-50/50 p-3 space-y-2">
                      {rijen.length === 0 ? (
                        <div className="flex items-center justify-between gap-3 py-3 px-1">
                          <p className="text-sm text-gray-500">
                            Nog niemand benaderd voor deze discipline{locationId ? ' op deze vestiging' : ''}.
                          </p>
                          <Button size="sm" variant="outline" onClick={() => (window.location.href = '/employer/pool-uitnodigingen')}>
                            Nodig professionals uit
                          </Button>
                        </div>
                      ) : (
                        rijen.map((r) => (
                          <OnderhandelingRijKaart
                            key={r.id}
                            r={r}
                            isBezig={bezig === r.id}
                            uitgeklapt={uitgeklapt === r.id}
                            onToggleUitklap={() => onToggleUitklap(r.id)}
                            onHerinner={() => onHerinner(r)}
                            onTrekIn={() => onTrekIn(r)}
                            onAccepteerTegenbod={() => onAccepteerTegenbod(r)}
                            onTegenbodOpenen={() => onTegenbodOpenen(r)}
                            onAfwijzenOpenen={() => onAfwijzenOpenen(r)}
                            onArchiveer={() => onArchiveer(r)}
                            onTender={() => onTender(r)}
                            onOpenContract={() => onOpenContract(r)}
                          />
                        ))
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PoolDekkingsDashboard;
